import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { CustomScheduleVisit } from 'components/Assessments/components/VisitScheduling/types'
import { SectionAdapter } from 'components/Assessments/adapters/types'
import { AdapterOptions } from 'components/Assessments/adapters/types'
import { RecertFormData } from '../../types/RecertFormData'
import { RecertFormField } from '../../types/RecertFormField'
import { RecertAssessmentQuestionnaireItem } from '../../types/questionnaire'
import {
  extractPeriodStartAndEndTimesWithTimezone,
  getAnswerValuesFromItems
} from '../../../helpers'
import {
  convertAppointmentTimeFormDataToFlatAnswers,
  convertTimeToUTCString
} from '../../../helpers.uncontrolled'
import {
  convertCustomScheduleToFlatAnswers,
  convertOtherAppointmentsToFlatAnswers,
  extractCustomSchedule,
  extractCustomScheduleByWeekday,
  extractOtherAppointments
} from '../../helpers/visitScheduling'

export class VisitSchedulingAdapter extends SectionAdapter<RecertFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    options?: AdapterOptions
  ): Partial<RecertFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const wakeupTime = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME
    )?.at(0)

    const bedtime = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME
    )?.at(0)

    const schoolTimes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY
    )

    const schoolTimesPeriod = extractPeriodStartAndEndTimesWithTimezone(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
      options?.caregiverTimezone
    )

    const otherAppointments = extractOtherAppointments(flatAnswersArray, options)
    const customSchedule = extractCustomSchedule(flatAnswersArray, options)
    const customScheduleByWeekday = extractCustomScheduleByWeekday(customSchedule)
    const editedCustomSchedule = []
    const additionalNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES
    )?.at(0)
    const changeReason = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON
    )?.at(0)

    const initialsAuthorization = getAnswerValuesFromItems(
      flatAnswersArray,
      RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_INITIALS_AUTHORIZATION
    )?.at(0)

    return {
      [RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME as string]: wakeupTime,
      [RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME]: bedtime,
      [RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY]: schoolTimes,
      [RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD]: schoolTimesPeriod,
      [RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS]: otherAppointments,
      // Populate both daily and weekly visit schedule fields from FHIR
      // Daily mode uses flat array, weekly mode uses grouped by weekday
      [RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]: customSchedule,
      [RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY]: customScheduleByWeekday,
      [RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_EDITED_VISITS]: editedCustomSchedule,
      [RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES]: additionalNotes,
      [RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON]: changeReason,
      [RecertFormField.VISIT_SCHEDULING_INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(formData: RecertFormData): Array<{ linkId: string; value: unknown }> {
    const mutations: Array<{ linkId: string; value: unknown }> = []
    const wakeupTime = formData[RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME]
    const wakeUpTimeUTC = wakeupTime ? convertTimeToUTCString(wakeupTime) : undefined

    const bedtime = formData[RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME]
    const bedtimeUTC = bedtime ? convertTimeToUTCString(bedtime) : undefined

    const schoolTimesPeriod =
      formData[RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD]
    const schoolTimesPeriodUTC = schoolTimesPeriod
      ? convertAppointmentTimeFormDataToFlatAnswers(schoolTimesPeriod)
      : undefined
    const otherAppointments = convertOtherAppointmentsToFlatAnswers(
      formData[RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS]
    )

    // Handle both daily and weekly visit schedule modes
    // Weekly mode: visits grouped by weekday (preferred for weekly/shift scheduling)
    // Daily mode: flat array of visits (used for daily/visit scheduling)
    const visitsByWeekday =
      formData[RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS_BY_WEEKDAY]
    const dailyVisits = formData[RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS]

    // Determine which source to use based on what has data
    // Prioritize visitsByWeekday if it has data, otherwise use dailyVisits
    let flatVisits: CustomScheduleVisit[] | undefined = undefined
    if (visitsByWeekday && visitsByWeekday.length > 0) {
      // Weekly mode: flatten the grouped structure
      flatVisits = visitsByWeekday.flatMap(({ visits }) => visits)
    } else if (dailyVisits && dailyVisits.length > 0) {
      // Daily mode: already flat
      flatVisits = dailyVisits.map((visit) => ({
        ...visit,
        weekday: null
      }))
    }
    const customSchedule = flatVisits ? convertCustomScheduleToFlatAnswers(flatVisits) : undefined

    if (customSchedule) {
      mutations.push({
        linkId: RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_VISITS,
        value: customSchedule
      })
    }

    mutations.push(
      ...[
        {
          linkId: RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_WAKEUP_TIME,
          value: wakeUpTimeUTC
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_BEDTIME,
          value: bedtimeUTC
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY,
          value: formData[RecertFormField.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_DAY]
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_START,
          value: schoolTimesPeriodUTC?.appointmentTimeValues?.at(0)
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_SCHOOL_TIMES_PERIOD_END,
          value: schoolTimesPeriodUTC?.appointmentTimeValues?.at(1)
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_PATIENT_SCHEDULE_OTHER_APPOINTMENTS,
          value: otherAppointments
        },
        {
          linkId:
            RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES,
          value: formData[RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_ADDITIONAL_NOTES]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON,
          value: formData[RecertFormField.VISIT_SCHEDULING_CUSTOM_SCHEDULE_CHANGE_REASON]
        },
        {
          linkId: RecertAssessmentQuestionnaireItem.VISIT_SCHEDULING_INITIALS_AUTHORIZATION,
          value: formData[RecertFormField.VISIT_SCHEDULING_INITIALS_AUTHORIZATION]
        }
      ]
    )

    return mutations
  }
}
